import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Api from "../Auth/Api";
import Clappr from 'clappr';
import { buildPublicFilesUrl, buildScormPlayerUrl, isScormStyleLaunchPath, isUrlOurPublicFilesPackage, normalizeScormLaunchSegment } from '../utils/publicFilesUrl';

function Aula() {

    const navigate = useNavigate();
    const token = JSON.parse(localStorage.getItem("user_token"));
    const { id } = useParams();
    const tempoAula = process.env.REACT_APP_TEMPO_AULA;
    const isMobileAndHorizontal = window.matchMedia('(orientation: landscape)').matches;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const clapprPlayerRef = useRef(null);
    const contentRef = useRef(null);
    const progressIntervalRef = useRef(null);
    const fullscreenContainerRef = useRef(null);

    const [dataInfoAula, setDataInfoAula] = useState([]);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const arquivoPrincipal = dataInfoAula[0] && JSON.parse(dataInfoAula[0].arquivos)[0]
    const validExtensions = useMemo(() => ["mp4", "avi", "mkv", "mov"], []);

    // Função para obter o MIME type baseado na extensão
    const getMimeType = (extension) => {
        const mimeTypes = {
            'mp4': 'video/mp4',
            'avi': 'video/x-msvideo',
            'mkv': 'video/x-matroska',
            'mov': 'video/quicktime',
            'webm': 'video/webm'
        };
        return mimeTypes[extension?.toLowerCase()] || 'video/mp4';
    };

    let urlPrincipal = '';
    const html_externo = dataInfoAula[0] && dataInfoAula[0].html_externo;
    const extAula = dataInfoAula[0] && (dataInfoAula[0].extensao || '').toLowerCase();
    const scormEditionDb = dataInfoAula[0] && dataInfoAula[0].scorm_edition;
    const isScormLesson = !!(dataInfoAula[0] && (
        scormEditionDb === '1.2' || scormEditionDb === '2004'
        || extAula === 'scorm'
        || (extAula === 'html' && !dataInfoAula[0].html_externo)
        || (extAula === 'zip' && arquivoPrincipal)
        || isScormStyleLaunchPath(arquivoPrincipal)
        || (html_externo && isUrlOurPublicFilesPackage(html_externo, process.env.REACT_APP_API_URL, process.env.REACT_APP_FILES_URL))
    ));
    if (isScormLesson) {
        const ed = scormEditionDb === '2004' ? '2004' : '1.2';
        const launchSeg = normalizeScormLaunchSegment(arquivoPrincipal);
        urlPrincipal = buildScormPlayerUrl(process.env.REACT_APP_API_URL, id, launchSeg, token, ed);
    } else if (html_externo) {
        urlPrincipal = html_externo;
    } else {
        urlPrincipal = buildPublicFilesUrl(process.env.REACT_APP_FILES_URL, arquivoPrincipal, token);
    }

    let listaArquivos = [];

    if (dataInfoAula[0] && dataInfoAula[0].arquivos) {
        const parsedData = JSON.parse(dataInfoAula[0].arquivos);
        if (Array.isArray(parsedData)) {
            listaArquivos = parsedData;
        }
    }

    // Agora, você pode prosseguir com as operações no array, como filtragem:
    listaArquivos = listaArquivos.filter(item => item);

    // Remover o item com índice 0
    listaArquivos.splice(0, 1);

    // Funções para gerenciar progresso no localStorage
    const getStorageKey = useCallback(() => `aula_progress_${id}`, [id]);

    const loadProgress = useCallback(() => {
        try {
            const saved = localStorage.getItem(getStorageKey());
            if (saved) {
                const { time, completed } = JSON.parse(saved);
                if (!completed) {
                    return time || 0;
                }
            }
        } catch (error) {
            console.error('❌ Erro ao carregar progresso:', error);
        }
        return 0;
    }, [getStorageKey]);

    const saveProgress = useCallback((time, completed = false) => {
        try {
            const progressData = {
                time,
                completed,
                lastUpdate: new Date().toISOString()
            };
            localStorage.setItem(getStorageKey(), JSON.stringify(progressData));
        } catch (error) {
            console.error('❌ Erro ao salvar progresso:', error);
        }
    }, [getStorageKey]);

    const clearProgress = useCallback(() => {
        try {
            localStorage.removeItem(getStorageKey());
        } catch (error) {
            console.error('❌ Erro ao limpar progresso:', error);
        }
    }, [getStorageKey]);

    // Funções para gerenciar fullscreen
    const toggleFullscreen = useCallback(() => {
        const container = fullscreenContainerRef.current;
        if (!container) return;

        if (!isFullscreen) {
            // Entrar em fullscreen
            if (container.requestFullscreen) {
                container.requestFullscreen();
            } else if (container.webkitRequestFullscreen) {
                container.webkitRequestFullscreen();
            } else if (container.mozRequestFullScreen) {
                container.mozRequestFullScreen();
            } else if (container.msRequestFullscreen) {
                container.msRequestFullscreen();
            }
        } else {
            // Sair do fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    }, [isFullscreen]);

    // Listener para detectar mudanças no fullscreen
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            );
            setIsFullscreen(isCurrentlyFullscreen);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`api/info-aula/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataInfoAula(response.data);
                // Carregar progresso salvo
                loadProgress();
            } catch (error) {
                console.error(error.response.data.message)
                navigate("/");
            }
        }

        fetchData();
    }, [token, id, navigate, loadProgress]);


    // Inicializar Clappr player
    useEffect(() => {
        if (!dataInfoAula[0] || !arquivoPrincipal) return;

        // Destruir player anterior se existir
        if (clapprPlayerRef.current) {
            clapprPlayerRef.current.destroy();
            clapprPlayerRef.current = null;
        }

        // Status de visualização
        let marcadoComoAssistido = false;
        let lastSavedTime = 0;
        let accumulatedTime = 0;

        // Função para marcar como assistido com retry
        const marcarComoAssistido = async (tentativas = 3) => {
            if (marcadoComoAssistido) {
                return;
            }

            try {
                await Api.post(`api/progresso-aula/${id}`, { data: 'assistida' }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                marcadoComoAssistido = true;
                saveProgress(0, true);
                clearProgress();
            } catch (error) {
                console.error('Erro ao marcar aula como assistida:', error);
                if (tentativas > 0) {
                    setTimeout(() => marcarComoAssistido(tentativas - 1), 5000);
                }
            }
        };

        // Para conteúdo de vídeo - usar Clappr
        if (validExtensions.includes(dataInfoAula[0].extensao)) {
            const mimeType = getMimeType(dataInfoAula[0].extensao);

            const player = new Clappr.Player({
                parentId: '#clappr-player-container',
                source: urlPrincipal,
                mimeType: mimeType,
                width: '100%',
                height: '100%',
                autoPlay: false,
                mute: false,
                preload: 'metadata',
                events: {
                    onReady: () => {
                        console.log('Clappr player ready');
                    },
                    onLoadedMetadata: () => {
                        const duration = player.getDuration();
                        console.log('Metadados carregados - Duração do vídeo:', duration);
                    },
                    onPlay: () => {
                        console.log('Clappr player started playing');
                    },
                    onEnded: () => {
                        if (!marcadoComoAssistido) {
                            marcarComoAssistido();
                        }
                    },
                    onTimeUpdate: () => {
                        
                        // Incrementar tempo acumulado (baseado em tempoAula, não duração do vídeo)
                        accumulatedTime += 1; // Incrementa 1 segundo a cada chamada
                        
                        
                        // Salvar progresso a cada 5 segundos
                        if (accumulatedTime - lastSavedTime >= 5) {
                            saveProgress(accumulatedTime, false);
                            lastSavedTime = accumulatedTime;
                        }
                        
                        // Marcar como assistido quando atingir o tempoAula
                        const requiredTime = parseInt(tempoAula) || 60; // tempoAula em segundos
                        if (accumulatedTime >= requiredTime && !marcadoComoAssistido) {
                            marcarComoAssistido();
                        }
                    }
                }
            });

            clapprPlayerRef.current = player;
        } else {
            // Para conteúdo não-vídeo (PDF, iframe, etc)
            if (dataInfoAula[0].extensao && dataInfoAula[0].extensao.toLowerCase() === 'pdf') {
                marcarComoAssistido();
                return;
            }

            const row = dataInfoAula[0];
            const ext = (row.extensao || '').toLowerCase();
            const scEd = row.scorm_edition;
            let principal0 = null;
            try {
                principal0 = row.arquivos ? JSON.parse(row.arquivos)[0] : null;
            } catch (e) {
                principal0 = null;
            }
            const hex = row.html_externo;
            const isScorm = scEd === '1.2' || scEd === '2004'
                || (ext === 'html' && !row.html_externo)
                || (ext === 'zip' && principal0)
                || isScormStyleLaunchPath(principal0)
                || (hex && isUrlOurPublicFilesPackage(hex, process.env.REACT_APP_API_URL, process.env.REACT_APP_FILES_URL));
            if (isScorm) {
                return () => {};
            }

            // Sistema híbrido para outros conteúdos
            let accumulatedTime = loadProgress();
            let isVisible = false;
            let isPageVisible = document.visibilityState === 'visible';
            let progressInterval = null;

            const requiredTime = tempoAula;

            const updateProgress = () => {
                if (isVisible && isPageVisible && !marcadoComoAssistido) {
                    accumulatedTime += 1;

                    if (accumulatedTime % 5 === 0) {
                        saveProgress(accumulatedTime, false);
                    }

                    if (accumulatedTime >= requiredTime) {
                        marcarComoAssistido();
                    }
                }
            };

            const observerCallback = (entries) => {
                entries.forEach(entry => {
                    isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
                });
            };

            const observer = new IntersectionObserver(observerCallback, {
                threshold: [0, 0.5, 1.0]
            });

            const waitForElement = setInterval(() => {
                const contentElement = document.querySelector('.iframe-container, .responsive-iframe-wrapper');
                if (contentElement) {
                    clearInterval(waitForElement);

                    observer.observe(contentElement);
                    contentRef.current = contentElement;

                    progressInterval = setInterval(updateProgress, 1000);
                    progressIntervalRef.current = progressInterval;
                }
            }, 100);

            const handleVisibilityChange = () => {
                isPageVisible = document.visibilityState === 'visible';
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);

            return () => {
                clearInterval(waitForElement);
                if (progressInterval) {
                    clearInterval(progressInterval);
                }
                if (observer) {
                    observer.disconnect();
                }
                document.removeEventListener('visibilitychange', handleVisibilityChange);

                if (!marcadoComoAssistido && accumulatedTime > 0) {
                    saveProgress(accumulatedTime, false);
                }
            };
        }

        return () => {
            if (clapprPlayerRef.current) {
                clapprPlayerRef.current.destroy();
                clapprPlayerRef.current = null;
            }
        };
    }, [dataInfoAula, id, token, validExtensions, tempoAula, loadProgress, saveProgress, clearProgress, arquivoPrincipal, urlPrincipal]);

    // replace: evita empilhar /aulas por cima de /aula — senão navigate(-1) na lista volta pra mesma aula (loop).
    const voltarPaginaAnterior = useCallback(() => {
        const idModulo = dataInfoAula[0]?.idModulo;
        if (idModulo != null && idModulo !== '') {
            navigate(`/aulas/${idModulo}`, { replace: true });
            return;
        }
        navigate('/');
    }, [dataInfoAula, navigate]);

    return (
        <>
            {dataInfoAula[0] &&
                <div className="fade-in w-100 mb-5">

                    {/* Desktop: Voltar | título da aula */}
                    <div className="d-none d-md-flex align-items-center gap-2 gap-md-3 mb-4 flex-wrap">
                        <button
                            type="button"
                            onClick={voltarPaginaAnterior}
                            className="btn btn-outline-primary rounded-pill px-4 py-1 d-flex align-items-center gap-2 shadow-sm flex-shrink-0"
                            style={{
                                transition: 'all 0.3s ease',
                                borderWidth: '2px',
                                fontWeight: '500'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateX(-5px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 110, 253, 0.3)';
                                e.currentTarget.style.backgroundColor = '#0d6efd';
                                e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateX(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#0d6efd';
                            }}
                        >
                            <i className="bi bi-arrow-left fs-5"></i>
                            <span>Voltar</span>
                        </button>
                        <div className="flex-grow-1 d-flex justify-content-end align-items-center px-1 min-w-0">
                            <span className="badge bg-primary rounded-pill px-3 py-2 text-truncate" title={dataInfoAula[0].titulo}>
                                <i className="bi bi-play-circle me-1"></i>
                                {dataInfoAula[0].titulo}
                            </span>
                        </div>
                    </div>

                    {/* Mobile: sem banner/thumbnail — só Voltar + título compacto */}
                    <div className="d-md-none mb-2">
                        <div className="d-flex align-items-center gap-2 py-1 min-w-0">
                            <button
                                type="button"
                                onClick={voltarPaginaAnterior}
                                className="btn btn-outline-primary rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center"
                                style={{
                                    width: '36px',
                                    height: '36px',
                                    padding: 0,
                                    transition: 'all 0.2s ease'
                                }}
                                aria-label="Voltar"
                            >
                                <i className="bi bi-arrow-left fs-6"></i>
                            </button>
                            <p className="mb-0 fw-semibold text-dark text-truncate flex-grow-1 small" title={dataInfoAula[0].titulo}>
                                {dataInfoAula[0].titulo}
                            </p>
                        </div>
                    </div>

                    {arquivoPrincipal !== undefined &&
                        <div className="body">
                            <section className="w-100 position-relative">
                                {validExtensions.includes(dataInfoAula[0].extensao) ? (
                                    <div
                                        className="video-container rounded-3 overflow-hidden w-100 bg-dark position-relative"
                                    >
                                        <div
                                            id="clappr-player-container"
                                            className="aula-video-player-container"
                                            style={{
                                                flex: '1 1 0%',
                                                width: '100%',
                                                height: isMobile ? '60vh' : '70vh',
                                                minHeight: isMobile ? '250px' : '400px',
                                                maxHeight: isMobile ? '60vh' : '70vh',
                                                display: 'block',
                                                visibility: 'visible',
                                                opacity: 1,
                                                position: 'relative',
                                                backgroundColor: '#000'
                                            }}
                                        ></div>
                                    </div>
                                ) : (
                                    <div
                                        ref={fullscreenContainerRef}
                                        className="iframe-container rounded-3 overflow-hidden w-100 bg-dark position-relative"
                                    >
                                        <button
                                            type="button"
                                            onClick={toggleFullscreen}
                                            className="btn btn-light position-absolute top-0 end-0 m-3 rounded-circle d-flex align-items-center justify-content-center"
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                zIndex: 10,
                                                opacity: 0.8
                                            }}
                                            title={isFullscreen ? "Sair da tela cheia" : "Tela cheia"}
                                        >
                                            <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`}></i>
                                        </button>
                                        <div className="responsive-iframe-wrapper"
                                            style={{
                                                position: 'relative',
                                                paddingTop: isMobile && isMobileAndHorizontal ? '80vh' : isMobile ? '120%' : '70vh',
                                                width: '100%',
                                                backgroundColor: '#000',
                                                maxHeight: isMobile && isMobileAndHorizontal ? '100vh' : 'none'
                                            }}>
                                            <iframe
                                                className="responsive-iframe"
                                                src={urlPrincipal}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    border: 'none',
                                                    objectFit: 'contain'
                                                }}
                                                title={dataInfoAula[0].titulo}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            ></iframe>
                                        </div>
                                    </div>
                                )}
                            </section>
                        </div>
                    }

                    <div className="card mt-3 mb-5 py-3 px-4 bg-primary text-white back-image border-none">
                            <div className="d-flex align-items-center row g-0">
                                {
                                    dataInfoAula[0].arquivos &&
                                    listaArquivos.map((value, index) => {
                                        return (
                                            <div key={index} className="col-md-3 overflow-hidden bg-white text-center rounded m-2">
                                                <span className="d-flex bg-grey w-100 space-grey"></span>
                                                <Link target="_blank" to={buildPublicFilesUrl(process.env.REACT_APP_FILES_URL, value, token)}>
                                                    <button type="button" className="btn fs-6 text-primary link py-3">
                                                        Baixar conteúdo <i className="bi bi-download ms-2"></i>
                                                    </button>
                                                </Link>
                                            </div>
                                        )
                                    })
                                }


                                <div className={listaArquivos.length === 0 ? "col-12" : "col-md-3"}>
                                    <div className="card-body">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <span className="fs-4 fw-500">{dataInfoAula[0].titulo}</span>
                                        </div>
                                        <span className="fw-light text-break">
                                            {dataInfoAula[0].descricao}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                </div >
            }

        </>
    )
}

export default Aula;